import * as THREE from 'three';
import { $selectedObjects } from '../stores/selectionStore';
import { SceneObject } from './sceneObject';
import type {
  ActiveSelectionTool,
  SelectionDragSession,
} from './selectionTools/activeSelectionTool';
import { MoveSelectionTool } from './selectionTools/moveSelectionTool';
import { ResizeSelectionTool } from './selectionTools/resizeSelectionTool';
import { RotateSelectionTool } from './selectionTools/rotateSelectionTool';
import { SelectSelectionTool } from './selectionTools/selectSelectionTool';
import type { EditorTool } from '@/stores/toolStore';

export class ActiveSelection extends SceneObject {
  private readonly bounds = new THREE.Box3();
  private readonly scratchBounds = new THREE.Box3();
  private readonly helper = new THREE.Box3Helper(this.bounds, 0xfff1a8);
  private readonly center = new THREE.Vector3();
  private readonly size = new THREE.Vector3();
  private readonly tools: Map<EditorTool, ActiveSelectionTool>;
  private activeTool: EditorTool = 'select';
  private dragSession: SelectionDragSession | null = null;

  constructor() {
    super({});
    this.helper.visible = false;
    this.tools = new Map<EditorTool, ActiveSelectionTool>([
      ['select', new SelectSelectionTool()],
      ['move', new MoveSelectionTool()],
      ['resize', new ResizeSelectionTool()],
      ['rotate', new RotateSelectionTool()],
    ]);
  }

  protected renderObject(parent: THREE.Object3D): void {
    parent.add(this.helper);

    for (const tool of this.tools.values()) {
      tool.render(parent);
      tool.setVisible(false);
    }
  }

  override getPickTargets(): THREE.Object3D[] {
    if (!this.helper.visible) {
      return [];
    }

    const activeTool = this.tools.get(this.activeTool);
    if (!activeTool) {
      return [];
    }

    return activeTool.getPickTargets();
  }

  isDragging() {
    return this.dragSession !== null;
  }

  startDrag(
    intersection: THREE.Intersection,
    ray: THREE.Ray,
    camera: THREE.Camera,
  ) {
    const selectedObjects = $selectedObjects.get();
    if (selectedObjects.length === 0) {
      return false;
    }

    const activeTool = this.tools.get(this.activeTool);
    if (!activeTool) {
      return false;
    }

    const dragSession = activeTool.startDrag({
      intersection,
      ray,
      camera,
      center: this.center,
      size: this.size,
      selectedObjects,
    });
    if (!dragSession) {
      return false;
    }

    this.dragSession = dragSession;

    return true;
  }

  updateDrag(ray: THREE.Ray, snapToHalfStep = false) {
    if (!this.dragSession) {
      return;
    }

    this.dragSession.updateDrag(ray, snapToHalfStep);
  }

  endDrag() {
    if (this.dragSession?.endDrag) {
      this.dragSession.endDrag();
    }
    this.dragSession = null;
  }

  setActiveTool(tool: EditorTool) {
    if (this.activeTool === tool) {
      return;
    }

    this.activeTool = tool;
    this.endDrag();
    const toolsVisible = this.helper.visible;
    for (const [toolId, selectionTool] of this.tools.entries()) {
      selectionTool.setVisible(toolsVisible && toolId === this.activeTool);
    }
  }

  override update(_delta: number): void {
    const selectedObjects = $selectedObjects.get();
    if (selectedObjects.length === 0) {
      this.helper.visible = false;
      for (const tool of this.tools.values()) {
        tool.setVisible(false);
      }
      this.endDrag();
      return;
    }

    let hasBounds = false;
    for (const selectedObject of selectedObjects) {
      const worldBounds = selectedObject.getWorldAabb(this.scratchBounds);

      if (!hasBounds) {
        this.bounds.copy(worldBounds);
        hasBounds = true;
      } else {
        this.bounds.union(worldBounds);
      }
    }

    this.helper.box.copy(this.bounds);
    this.helper.visible = hasBounds;

    if (hasBounds) {
      this.bounds.getCenter(this.center);
      this.bounds.getSize(this.size);

      for (const [toolId, tool] of this.tools.entries()) {
        tool.updateTransform(this.center, this.size);
        tool.setVisible(toolId === this.activeTool);
      }
    } else {
      for (const tool of this.tools.values()) {
        tool.setVisible(false);
      }
    }
  }

  override dispose(): void {
    super.dispose();
    this.helper.geometry.dispose();

    if (Array.isArray(this.helper.material)) {
      this.helper.material.forEach(material => material.dispose());
    } else {
      this.helper.material.dispose();
    }

    for (const tool of this.tools.values()) {
      tool.dispose();
    }
  }
}
